import { FC, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  Paper,
  Chip,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { AreaControllerData } from '@/utilities/types/table'

const infoBoxStyle = {
  bgcolor: '#F3F4F6',
  border: '1px solid',
  borderColor: 'rgb(226, 232, 240)',
  borderRadius: '4px',
  p: 2,
  width: '100%',
}

interface AssignControllerFormData {
  supervisor: string
  userAreaControllerIds: string[]
}

interface ModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: AssignControllerFormData) => void
  formData: AssignControllerFormData
  setFormData: (data: AssignControllerFormData) => void
  areaControllers: AreaControllerData[]
  existingControllers?: AreaControllerData[]
  supervisorName: string
  isLoading?: boolean
  error?: string | null
}

export const AssignAreaControllerModal: FC<ModalProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  areaControllers = [],
  existingControllers = [],
  supervisorName,
  isLoading = false,
  error = null,
}) => {
  // Debug logging
  useEffect(() => {
    console.log('Modal Props:', {
      areaControllers,
      existingControllers,
      formData,
      supervisorName,
    })
  }, [areaControllers, existingControllers, formData, supervisorName])

  // Combine and deduplicate controllers
  const allControllers = useMemo(() => {
    // Create a Map to store unique controllers by sourceUserId
    const controllersMap = new Map<number, AreaControllerData>()

    // First add all existing controllers
    existingControllers.forEach((controller) => {
      controllersMap.set(controller.sourceUserId, controller)
    })

    // Then add all available controllers, potentially overwriting duplicates
    areaControllers.forEach((controller) => {
      if (!controllersMap.has(controller.sourceUserId)) {
        controllersMap.set(controller.sourceUserId, controller)
      }
    })

    const uniqueControllers = Array.from(controllersMap.values())
    console.log('All unique controllers:', uniqueControllers)
    return uniqueControllers
  }, [areaControllers, existingControllers])

  // Filter available controllers - those not already assigned
  const availableControllers = useMemo(() => {
    const available = allControllers.filter(
      (controller) =>
        !formData.userAreaControllerIds.includes(
          controller.sourceUserId.toString(),
        ),
    )
    console.log('Available controllers:', available)
    return available
  }, [allControllers, formData.userAreaControllerIds])

  // Get assigned controllers
  const assignedControllers = useMemo(() => {
    const assigned = allControllers.filter((controller) =>
      formData.userAreaControllerIds.includes(
        controller.sourceUserId.toString(),
      ),
    )
    console.log('Assigned controllers:', assigned)
    return assigned
  }, [allControllers, formData.userAreaControllerIds])

  const handleAdd = (event: SelectChangeEvent<string>) => {
    const controllerId = event.target.value
    console.log('Adding controller:', controllerId)
    if (controllerId) {
      setFormData({
        ...formData,
        userAreaControllerIds: [
          ...formData.userAreaControllerIds,
          controllerId,
        ],
      })
    }
  }

  const handleRemove = (controllerId: string) => {
    console.log('Removing controller:', controllerId)
    setFormData({
      ...formData,
      userAreaControllerIds: formData.userAreaControllerIds.filter(
        (id) => id !== controllerId,
      ),
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Assign Area Controllers</Typography>
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            sx={{ width: 20, height: 20 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Supervisor Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Supervisor
          </Typography>
          <Paper variant="outlined" sx={infoBoxStyle}>
            <Typography>{supervisorName}</Typography>
          </Paper>
        </Box>

        {/* Assigned Controllers Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assigned Area Controllers
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, minHeight: '80px' }}>
            {assignedControllers.length === 0 ? (
              <Typography color="text.secondary">
                No area controllers assigned
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {assignedControllers.map((controller) => (
                  <Paper
                    key={controller.sourceUserId}
                    variant="outlined"
                    sx={{ p: 2 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography>
                        {controller.firstName} {controller.lastName}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          handleRemove(controller.sourceUserId.toString())
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                    {controller.areas && controller.areas.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Areas handled:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          {controller.areas.map((area) => (
                            <Chip
                              key={area.id}
                              label={area.areaName}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Add Controllers Dropdown */}
        <FormControl fullWidth>
          <InputLabel id="add-controllers-label">
            Add Area Controllers
          </InputLabel>
          <Select
            labelId="add-controllers-label"
            value=""
            onChange={handleAdd}
            label="Add Area Controllers"
            disabled={isLoading || availableControllers.length === 0}
          >
            {availableControllers.map((controller) => (
              <MenuItem
                key={controller.sourceUserId}
                value={controller.sourceUserId.toString()}
              >
                <Box sx={{ py: 1 }}>
                  <Typography>
                    {controller.firstName} {controller.lastName}
                  </Typography>
                  {controller.areas && controller.areas.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Areas:{' '}
                      {controller.areas.map((area) => area.areaName).join(', ')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(formData)}
          disabled={isLoading}
        >
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
